import type { SavedRoofModulesLayout } from '../../../../../@types/types';
import {
    addModulesToAllRoofsMaxFill,
    SetModuleGraph,
} from '../../Three-JS-Viewer/Controllers/ModuleControl';
import {
    clearAllModuleMeshes,
    restoreModulesLayoutFromSnapshot,
} from '../../Three-JS-Viewer/Controllers/DefaultLayoutControl';
import {
    getCategoryMainLayoutKey,
    normalizeLayoutMinEff,
    resolveCategoryApplyLayoutParams,
    type CategoryLayoutCandidate,
} from './categoryBestVariantScoring';
import { resolveAiLayoutRoofFaceIds } from './resolveAiLayoutRoofFaceIds';
import {
    countModulesInLayout,
    isSnapshotConsistentWithCard,
    logAiDesignResolve,
    readExpectedCardMetrics,
    readLiveViewerMetrics,
} from './aiDesignFlowDebug';
import {
    resolveModulesLayoutMatchingCount,
} from './aiLayoutCacheHydration';
import { resolveAltLayoutVariantLayoutKey } from './filterAltLayoutDisplayVariants';
import {
    countModulesApplicableInRemappedLayout,
    remapModulesLayoutToViewerWithMap,
    remapRoofFaceIdsToViewer,
} from './remapModulesLayoutRoofFaceIds';
import { useViewerStore } from '../../../../../store/store';

type LayoutRoofSummary = { roofFaceId?: string; roofNum?: unknown };

function readLayoutRoofSummariesForCategory(
    categoryLabel: string | undefined,
    layoutKey: string,
): LayoutRoofSummary[] | undefined {
    const state = useViewerStore.getState() as any;
    const summariesByKey = (state.layoutRoofSummariesByKey ?? {}) as Record<string, LayoutRoofSummary[]>;
    const catKey = categoryLabel ? getCategoryMainLayoutKey(categoryLabel) : undefined;
    const fromSummaries =
        (catKey ? summariesByKey[catKey] : undefined) ??
        (categoryLabel ? summariesByKey[categoryLabel] : undefined) ??
        summariesByKey[layoutKey];
    if (fromSummaries?.length) return fromSummaries;

    const shadeWeights = (state.aiModuleFillingScenarios?.[0]?.shadeWeights ?? []) as Array<{
        roofFaceId?: string;
        roofNum?: unknown;
    }>;
    const fromShade = shadeWeights
        .map((row) => ({
            roofFaceId: typeof row.roofFaceId === 'string' ? row.roofFaceId : undefined,
            roofNum: row.roofNum,
        }))
        .filter((row) => row.roofFaceId);
    return fromShade.length ? fromShade : undefined;
}

/** Module count from a snapshot rebound onto roofs present in the live viewer. */
export function countModulesApplicableInViewer(
    viewer: { roofMeshArr?: Array<{ roofFaceId?: string }> } | null | undefined,
    modulesLayout: SavedRoofModulesLayout | null | undefined,
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): number {
    if (!viewer?.roofMeshArr?.length || !Array.isArray(modulesLayout) || !modulesLayout.length) {
        return 0;
    }
    const remapped = remapModulesLayoutToViewerWithMap(viewer, modulesLayout, layoutRoofSummaries).layout;
    return countModulesApplicableInRemappedLayout(remapped);
}

/**
 * Whether a cached placement snapshot can be applied in the live viewer after roof-id remapping.
 */
export function isSnapshotApplicableToViewer(
    viewer: { roofMeshArr?: Array<{ roofFaceId?: string }> } | null | undefined,
    layoutKey: string,
    categoryLabel: string | undefined,
    modulesLayout: SavedRoofModulesLayout | null | undefined,
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): boolean {
    if (!isSnapshotConsistentWithCard(layoutKey, categoryLabel, modulesLayout)) return false;
    const summaries =
        layoutRoofSummaries ?? readLayoutRoofSummariesForCategory(categoryLabel, layoutKey);
    const applicable = countModulesApplicableInViewer(viewer, modulesLayout, summaries);
    if (applicable <= 0) return false;
    const cardModules = countModulesInLayout(modulesLayout);
    return applicable === cardModules;
}

export type ApplyCategoryLayoutInViewerArgs = {
    viewer: any;
    layoutKey: string;
    categoryLabel?: string;
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    layoutCandidatesByCategory?: Record<
        string,
        Array<{
            key?: string;
            roofFaceIds?: string[];
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            modulesLayout?: SavedRoofModulesLayout;
        }>
    >;
    variantsByCategory?: Record<string, unknown[]>;
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>;
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>;
    bestRoofsForLayout: { roofFaceIds?: string[] };
    defaultMaxfillRoofIds?: string[];
    simulateLayoutForRoofIds: (
        roofFaceIds: string[],
        opts?: {
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            layoutCacheKey?: string;
        },
    ) => Promise<{ modulesLayout?: SavedRoofModulesLayout } | void>;
    applyRoofSetInViewer: (
        roofFaceIds: string[],
        opts?: {
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            deferShadeCalc?: boolean;
        },
    ) => Promise<void>;
};

/**
 * Apply a catalog / cached AI category layout to the live 3D viewer.
 * Tries exact placement snapshots first, then re-simulates the roof set (same as Run AI),
 * with max-fill only as a last resort when roof ids cannot be resolved.
 */
export async function applyCategoryLayoutInViewer(
    args: ApplyCategoryLayoutInViewerArgs,
): Promise<SavedRoofModulesLayout | undefined> {
    const {
        viewer,
        layoutKey,
        categoryLabel,
        categoryMainLayoutByCategory = {},
        layoutCandidatesByCategory = {},
        variantsByCategory = {},
        layoutModulesLayoutByKey = {},
        roofCombinationsFromAi,
        bestRoofsForLayout,
        defaultMaxfillRoofIds = [],
        simulateLayoutForRoofIds,
        applyRoofSetInViewer,
    } = args;

    if (!viewer) return undefined;

    if (layoutKey === 'default') {
        clearAllModuleMeshes(viewer);
        addModulesToAllRoofsMaxFill(viewer);
        await SetModuleGraph(viewer);
        logAiDesignResolve({ branch: 'default-maxfill' });
        return undefined;
    }

    const categoryApply = resolveCategoryApplyLayoutParams(
        categoryLabel,
        categoryMainLayoutByCategory,
        layoutCandidatesByCategory,
        variantsByCategory,
        layoutModulesLayoutByKey,
    );

    const main = categoryLabel ? categoryMainLayoutByCategory?.[categoryLabel] : undefined;
    const expectedCardModules = readExpectedCardMetrics(layoutKey, categoryLabel).modules;
    const expectedModules =
        (expectedCardModules != null && expectedCardModules > 0 ? expectedCardModules : undefined) ??
        (main?.modules && main.modules > 0 ? main.modules : undefined);
    const catKey = categoryLabel ? getCategoryMainLayoutKey(categoryLabel) : undefined;
    const variantKey =
        categoryLabel && main?.variantId
            ? resolveAltLayoutVariantLayoutKey(categoryLabel, { id: main.variantId })
            : undefined;
    const modulesLayout = resolveModulesLayoutMatchingCount(
        layoutModulesLayoutByKey,
        [catKey, main?.key, categoryLabel, layoutKey, variantKey],
        expectedModules,
        categoryApply?.modulesLayout,
    );
    const layoutRoofSummaries = readLayoutRoofSummariesForCategory(categoryLabel, layoutKey);
    const remappedModulesLayout = modulesLayout?.length
        ? remapModulesLayoutToViewerWithMap(viewer, modulesLayout, layoutRoofSummaries).layout
        : undefined;

    if (
        remappedModulesLayout?.length &&
        isSnapshotApplicableToViewer(
            viewer,
            layoutKey,
            categoryLabel,
            modulesLayout,
            layoutRoofSummaries,
        )
    ) {
        logAiDesignResolve({
            branch: 'modules-layout-snapshot',
            modulesLayout: remappedModulesLayout,
            roofFaceIds: categoryApply?.roofFaceIds,
            dir: categoryApply?.dir ?? null,
        });
        await restoreModulesLayoutFromSnapshot(viewer, remappedModulesLayout, { skipShadeRefresh: true });
        if (readLiveViewerMetrics().modules > 0) {
            return remappedModulesLayout;
        }
        console.warn(
            '[AI-DESIGN-FLOW] placement snapshot matched the card but placed 0 modules — re-simulating roof set',
        );
    } else if (modulesLayout?.length) {
        console.warn(
            '[AI-DESIGN-FLOW] ignoring unusable placement snapshot — re-simulating roof set',
            {
                cardConsistent: isSnapshotConsistentWithCard(layoutKey, categoryLabel, modulesLayout),
                applicableModules: countModulesApplicableInViewer(
                    viewer,
                    modulesLayout,
                    layoutRoofSummaries,
                ),
                snapshotModules: countModulesInLayout(modulesLayout),
                remappedModules: countModulesApplicableInRemappedLayout(remappedModulesLayout),
            },
        );
    }

    let roofFaceIds = categoryApply?.roofFaceIds?.length ? categoryApply.roofFaceIds : [];
    const dir = categoryApply?.dir ?? null;
    const minEff = categoryApply?.minEff ?? null;

    roofFaceIds = remapRoofFaceIdsToViewer(viewer, roofFaceIds, modulesLayout, layoutRoofSummaries);

    if (!roofFaceIds.length) {
        roofFaceIds = remapRoofFaceIdsToViewer(
            viewer,
            resolveAiLayoutRoofFaceIds({
                layoutKey,
                categoryLabel,
                roofCombinationsFromAi,
                layoutCandidatesByCategory,
                bestRoofsForLayout,
                defaultMaxfillRoofIds,
            }),
            modulesLayout,
            layoutRoofSummaries,
        );
    }

    if (roofFaceIds.length > 0) {
        const layoutCacheKey = categoryLabel ? getCategoryMainLayoutKey(categoryLabel) : layoutKey;
        logAiDesignResolve({
            branch: 'simulate-roof-set',
            roofFaceIds,
            dir,
        });
        const simulated = await simulateLayoutForRoofIds(roofFaceIds, {
            dir,
            minEff: normalizeLayoutMinEff(minEff),
            layoutCacheKey,
        });
        const simulatedLayout = simulated?.modulesLayout;
        if (readLiveViewerMetrics().modules > 0) {
            return simulatedLayout?.length ? simulatedLayout : undefined;
        }

        logAiDesignResolve({
            branch: 'roof-set-fill',
            roofFaceIds,
            dir,
        });
        await applyRoofSetInViewer(roofFaceIds, {
            dir,
            minEff: normalizeLayoutMinEff(minEff),
            deferShadeCalc: true,
        });
        if (readLiveViewerMetrics().modules > 0) {
            return simulatedLayout?.length ? simulatedLayout : undefined;
        }
    }

    logAiDesignResolve({ branch: 'maxfill-fallback' });
    console.warn(
        '[AI-DESIGN-FLOW] could not resolve a specific layout for',
        categoryLabel ?? layoutKey,
        '— falling back to default max-fill.',
    );
    clearAllModuleMeshes(viewer);
    addModulesToAllRoofsMaxFill(viewer);
    await SetModuleGraph(viewer);
    return undefined;
}
